// client/src/scenes/fines/FineTypes.jsx

import React, { useState } from "react";
import {
  Box,
  useTheme, useMediaQuery,
  Snackbar,
  Alert,
  Button,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useMutation, gql } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import Header from "components/Header";
import FineTypeForm from "./FineTypeForm";

// ------- GraphQL -------
const GET_FINE_TYPES = gql`
  query {
    fineTypes {
      id
      name
      description
      defaultAmount
      paymentWindowDays
      isActive
      groupId
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const CREATE_FINE_TYPE = gql`
  mutation CreateFineType($newFineType: FineTypesInput!) {
    createFineType(newFineType: $newFineType) {
      id
      name
      description
      defaultAmount
      paymentWindowDays
      isActive
      groupId
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const UPDATE_FINE_TYPE = gql`
  mutation UpdateFineType($updatedFineType: FineTypesInput!) {
    updateFineType(updatedFineType: $updatedFineType) {
      id
      name
      description
      defaultAmount
      paymentWindowDays
      isActive
      groupId
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const EMPTY_FORM = {
  id: null,
  name: "",
  defaultAmount: "",
  description: "",
  paymentWindowDays: "",
};

// Helper: get RoleGroupId from LS or decode the JWT if missing
const getRoleGroupId = () => {
  const fromLS = localStorage.getItem("roleGroupId");
  if (fromLS) return Number(fromLS);

  const token = localStorage.getItem("token");
  if (!token) return NaN;

  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(json);
    const rg = payload.RoleGroupId ?? payload.roleGroupId;
    if (rg != null) {
      localStorage.setItem("roleGroupId", String(rg));
      return Number(rg);
    }
  } catch (e) {
    console.warn("Failed to decode JWT", e);
  }
  return NaN;
};

const FineTypes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Table data
  const { data, loading, refetch } = useQuery(GET_FINE_TYPES);

  // Mutation with errorPolicy so result may contain `errors` array we can parse
  const [createFineType] = useMutation(CREATE_FINE_TYPE, { errorPolicy: "all" });
  const [updateFineType] = useMutation(UPDATE_FINE_TYPE, { errorPolicy: "all" });

  // Local UI state
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Role guard (mirror backend authorization: RoleGroupId 1 or 2)
  const roleGroupId = getRoleGroupId();
  const canManage = roleGroupId === 1 || roleGroupId === 2;

  const extractGqlError = (errOrRes) => {
    // ApolloError shape
    if (errOrRes?.graphQLErrors?.length) {
      const extMsg = errOrRes.graphQLErrors[0]?.extensions?.message;
      return extMsg || errOrRes.graphQLErrors[0]?.message || "Unexpected error.";
    }
    // Result with errors due to errorPolicy: 'all'
    if (errOrRes?.errors?.length) {
      const extMsg = errOrRes.errors[0]?.extensions?.message;
      return extMsg || errOrRes.errors[0]?.message || "Unexpected error.";
    }
    // Fallback
    return errOrRes?.message || "Unexpected error.";
  };

  const getPaymentWindowValidationError = () => {
    const raw = formData.paymentWindowDays;
    if (raw === "" || raw == null) return "";
    const numericValue = Number(raw);
    if (!Number.isInteger(numericValue) || numericValue <= 0) {
      return "Payment window must be a whole number greater than 0.";
    }
    return "";
  };

  const openCreateDialog = () => {
    setMode("create");
    setFormData(EMPTY_FORM);
    setOpen(true);
  };

  const openEditDialog = (row) => {
    if (!canManage) return;

    setMode("edit");
    setFormData({
      id: row.id,
      name: row.name ?? "",
      defaultAmount: row.defaultAmount ?? "",
      description: row.description ?? "",
      paymentWindowDays: row.paymentWindowDays ?? "",
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!canManage) {
        setErrorMessage(
          mode === "edit"
            ? "You are not authorized to update fine types."
            : "You are not authorized to create fine types."
        );
        return;
      }

      const paymentWindowError = getPaymentWindowValidationError();
      if (paymentWindowError) {
        setErrorMessage(paymentWindowError);
        return;
      }

      const payload = {
        name: formData.name || null,
        description: formData.description || null,
        defaultAmount:
          formData.defaultAmount === "" ? null : Number(formData.defaultAmount),
        paymentWindowDays:
          formData.paymentWindowDays === "" ? null : Number(formData.paymentWindowDays),
      };

      if (mode === "edit") {
        const variables = {
          updatedFineType: {
            ...payload,
            id: Number(formData.id),
            updatedDate: null,
            updatedBy: null,
          },
        };

        const res = await updateFineType({ variables });
        if (res?.errors?.length) {
          setErrorMessage(extractGqlError(res));
          return;
        }

        if (res?.data?.updateFineType?.id) {
          await refetch();
          setSuccessMessage("Fine type updated successfully.");
          setOpen(false);
        } else {
          setErrorMessage("Failed to update fine type.");
        }
        return;
      }

      const variables = {
        newFineType: {
          ...payload,
          createdDate: null, // let backend set
          createdBy: null, // backend reads from auth context
        },
      };

      const res = await createFineType({ variables });

      // Handle GraphQL errors in the response
      if (res?.errors?.length) {
        setErrorMessage(extractGqlError(res));
        return;
      }

      if (res?.data?.createFineType?.id) {
        await refetch();
        setSuccessMessage("Fine type created successfully.");
        setOpen(false);
      } else {
        setErrorMessage("Failed to create fine type.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(extractGqlError(err));
    }
  };

  // Table columns
  const columns = [
    { field: "id", headerName: "ID", flex: 0.4, minWidth: 80 },
    { field: "name", headerName: "Name", flex: 1.2, minWidth: 160 },
    {
      field: "defaultAmount",
      headerName: "Default Amount",
      flex: 0.8,
      minWidth: 140,
      valueFormatter: ({ value }) =>
        value == null ? "-" : `KES ${Number(value).toLocaleString()}`,
    },
    {
      field: "paymentWindowDays",
      headerName: "Payment Window",
      flex: 0.8,
      minWidth: 170,
      valueFormatter: ({ value }) =>
        value == null ? "-" : `${Number(value).toLocaleString()} day(s)`,
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.6,
      minWidth: 120,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Active" : "Inactive"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
    { field: "description", headerName: "Description", flex: 2, minWidth: 220 },
    { field: "createdDate", headerName: "Created", flex: 0.9, minWidth: 160 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 100,
      renderCell: (params) => (
        <Tooltip
          title={canManage ? "Edit fine type" : "You are not authorized to update fine types."}
        >
          <span>
            <IconButton
              aria-label="edit fine type"
              onClick={() => openEditDialog(params.row)}
              disabled={!canManage}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header
        title="FINE TYPES"
        subtitle="Define and manage your group's fine templates"
      />

      <FineTypeForm
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        mode={mode}
        disabled={!canManage}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Tooltip
          title={canManage ? "" : "You are not authorized to create fine types."}
        >
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              disabled={!canManage}
            >
              Create Fine Type
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box
        mt="40px"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.primary.light,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.background.alt,
            color: theme.palette.secondary[100],
            borderTop: "none",
          },
        }}
      >
        <DataGrid
          loading={loading}
          rows={data?.fineTypes || []}
          getRowId={(row) => row.id}
          columns={columns}
        
          columnVisibilityModel={
            isMobile
              ? {
                description: false,
                createdDate: false,
              }
              : undefined
          }/>
      </Box>
    </Box>
  );
};

export default FineTypes;
